// Разделы «Настроек» (007; ux.md «Табеля», раздел 11; эталон — Settings в gr8r-studio): навигация
// группами слева, открытый раздел справа. В «Настройках» — всё, что настраивает сервис и доступ:
// настройки установки, люди и роли, профиль, пароль, сеансы, журнал действий, состояние сервиса
// и выкатки. Группы «Администрирование» в боковом меню нет: пункт «Настройки» — внизу меню.
//
// Адреса разделов, которые были пунктами меню (/users, /rights, /audit, /ops/status…), не
// меняются: на них ведут письма, уведомления, ссылки из журнала и базы знаний. Новые разделы —
// под /settings/….
import type { Operation, Permissions } from '@kp/contracts';
import {
  Activity,
  BookUser,
  Building2,
  FilePen,
  FileSpreadsheet,
  KeyRound,
  Keyboard,
  LifeBuoy,
  LockKeyhole,
  Mail,
  MailOpen,
  Mails,
  MonitorSmartphone,
  Rocket,
  Rows3,
  ScrollText,
  Settings,
  ShieldCheck,
  UserRound,
  Users,
} from '@kp/ui/icons';
import type { LucideIcon } from '@kp/ui';
import { can, sections, visibleNavItems } from './permissions.ts';
import type { NavSession } from './permissions.ts';

/**
 * Группы навигации по порядку (ux.md, раздел 11): Общие, Доступ, Уведомления (033), Личное,
 * Безопасность и Сервис (0.22.2) — последней, как служебная группа «Billing» в настройках эталона.
 */
export type SettingsGroup =
  'general' | 'access' | 'notifications' | 'personal' | 'security' | 'service';

export const SETTINGS_GROUPS: readonly { id: SettingsGroup; title: string }[] = [
  { id: 'general', title: 'Общие' },
  { id: 'access', title: 'Доступ' },
  { id: 'notifications', title: 'Уведомления' },
  { id: 'personal', title: 'Личное' },
  { id: 'security', title: 'Безопасность' },
  { id: 'service', title: 'Сервис' },
];

export interface SettingsItem {
  id: string;
  label: string;
  to: string;
  icon: LucideIcon;
  group: SettingsGroup;
  /** Хотя бы одна операция; пустой список — раздел есть у любого вошедшего. */
  operations: readonly Operation[];
  /**
   * Раздел-список (таблица с панелью и карточкой справа) — во всю ширину справа от навигации;
   * раздел-форма — до 720 px (README «Табеля», «Каркас экрана»).
   */
  wide?: boolean;
}

export const settingsItems: readonly SettingsItem[] = [
  // 009: «Компания» — корпоративные правила компании (домены), первым в «Общих», как Workspace
  // в эталоне. Меняет — settings.write, как «Справочник». 022 (решение 3 плана): только для
  // чтения раздел видят и те, кто управляет доступом к протоколам (minutes.access.manage —
  // контролёр, ведущий): внешний доступ им нужен при приглашении.
  {
    id: 'company',
    label: 'Компания',
    to: '/settings/company',
    icon: Building2,
    group: 'general',
    operations: [...sections.settings.operations, 'minutes.access.manage'],
  },
  { id: 'view', label: 'Вид', to: '/settings/view', icon: Rows3, group: 'general', operations: [] },
  {
    id: 'mail',
    label: 'Почта',
    to: '/settings/mail',
    icon: Mail,
    group: 'general',
    operations: sections.mail.operations,
  },
  // 008: «Письма» — список писем и ответных писем с карточкой справа, рядом с «Почтой». Свой адрес,
  // а не страница внутри /settings/mail: раздел-список во всю ширину (wide), «Почта» — форма.
  {
    id: 'letters',
    label: 'Письма',
    to: '/settings/letters',
    icon: MailOpen,
    group: 'general',
    operations: sections.letters.operations,
    wide: true,
  },
  // 042 (FR-050; contracts/ui.md): «Шаблоны писем» — сразу после «Писем»: там — что ушло, здесь —
  // какими словами. Только администратору (mail.templates). Широкий раздел: список — таблица,
  // страница письма — поля и предпросмотр в две колонки.
  {
    id: 'letterTemplates',
    label: 'Шаблоны писем',
    to: '/settings/letter-templates',
    icon: FilePen,
    group: 'general',
    operations: sections.letterTemplates.operations,
    wide: true,
  },
  // 010: «Протоколы» — схемы шаблонов с карточкой справа (раздел-список, как «Письма»; FR-001);
  // мастер подключения — страница раздела /settings/protocols/new.
  {
    id: 'protocols',
    label: 'Протоколы',
    to: '/settings/protocols',
    icon: FileSpreadsheet,
    group: 'general',
    operations: sections.protocols.operations,
    wide: true,
  },
  {
    id: 'directory',
    label: 'Справочник',
    to: '/settings/directory',
    icon: BookUser,
    group: 'general',
    operations: sections.settings.operations,
  },
  {
    id: 'users',
    label: 'Пользователи',
    to: '/users',
    icon: Users,
    group: 'access',
    operations: sections.users.operations,
    wide: true,
  },
  {
    id: 'rights',
    label: 'Роли и права',
    to: '/rights',
    icon: ShieldCheck,
    group: 'access',
    operations: sections.rights.operations,
    wide: true,
  },
  {
    id: 'support',
    label: 'Доступ сопровождения',
    to: '/support-access',
    icon: LifeBuoy,
    group: 'access',
    operations: sections.support.operations,
    wide: true,
  },
  // 005: ключи агента — рядом с доступом сопровождения: оба дают доступ к данным со стороны.
  {
    id: 'agentKeys',
    label: 'Ключи агента',
    to: '/agent-keys',
    icon: KeyRound,
    group: 'access',
    operations: sections.agentKeys.operations,
    wide: true,
  },
  // 033 (T011; FR-001, FR-002, FR-007): «Рассылка» — режим песочницы, её адреса и стоп-кран, группа
  // «Уведомления» (ux.md, раздел 11). Виден администратору клиента (settings.write) и контролёру
  // (mail.stop): контролёру — режим и число адресов только для чтения и «Опасная зона».
  {
    id: 'mailing',
    label: 'Рассылка',
    to: '/settings/mailing',
    icon: Mails,
    group: 'notifications',
    operations: sections.mailing.operations,
  },
  {
    id: 'profile',
    label: 'Профиль',
    to: '/profile',
    icon: UserRound,
    group: 'personal',
    operations: ['profile.read'],
  },
  {
    id: 'shortcuts',
    label: 'Клавиши',
    to: '/settings/shortcuts',
    icon: Keyboard,
    group: 'personal',
    operations: [],
  },
  {
    id: 'password',
    label: 'Пароль',
    to: '/settings/password',
    icon: LockKeyhole,
    group: 'security',
    operations: ['profile.change_password'],
  },
  {
    id: 'sessions',
    label: 'Сеансы',
    to: '/settings/sessions',
    icon: MonitorSmartphone,
    group: 'security',
    operations: [],
  },
  {
    id: 'audit',
    label: 'Журнал действий',
    to: '/audit',
    icon: ScrollText,
    group: 'security',
    operations: sections.audit.operations,
    wide: true,
  },
  // 0.22.2 (решение владельца 31.10.2026): «Состояние» и «Выкатки» — из группы меню «Сервис»
  // («Сопровождение» у сопровождения) в «Настройки»; права и адреса прежние. «Состояние» —
  // краткое у администратора клиента, полное у сопровождения (StatusPage).
  {
    id: 'status',
    label: 'Состояние',
    to: '/ops/status',
    icon: Activity,
    group: 'service',
    operations: sections.status.operations,
    wide: true,
  },
  {
    id: 'deployments',
    label: 'Выкатки',
    to: '/ops/deployments',
    icon: Rocket,
    group: 'service',
    operations: sections.deployments.operations,
    wide: true,
  },
];

/** Разделы, на которые у пользователя есть право, в порядке навигации. */
export function visibleSettingsItems(permissions: Permissions): SettingsItem[] {
  return settingsItems.filter((item) => can(permissions, item.operations));
}

/** «Настройки» без раздела открывают первый доступный раздел. */
export function firstSettingsPath(permissions: Permissions): string {
  return visibleSettingsItems(permissions)[0]?.to ?? '/profile';
}

/**
 * Раздел по адресу страницы: сам раздел или его страница внутри — карточка пользователя
 * /users/:id, «Мои выгрузки» /audit/exports.
 */
export function settingsItemFor(pathname: string): SettingsItem | undefined {
  return settingsItems.find((item) => pathname === item.to || pathname.startsWith(`${item.to}/`));
}

/** Раздел для палитры ⌘K, поиска и «Недавних»: подпись, адрес, иконка. */
export interface SectionLink {
  label: string;
  to: string;
  icon: LucideIcon;
  /** Пояснение серым: «Настройки» — у разделов настроек («Справочник» есть и в меню). */
  sub?: string;
}

/**
 * Все разделы, куда пользователь может перейти: пункты бокового меню, «Настройки», затем разделы
 * настроек. Палитра и поиск находят «Пользователей» и «Журнал действий», хотя в меню их нет.
 * session — кто вошёл (030): «Мои задачи» и «Главная» — как в меню (visibleNavItems).
 */
export function sectionLinks(permissions: Permissions, session?: NavSession): SectionLink[] {
  return [
    ...visibleNavItems(permissions, session),
    { label: 'Настройки', to: '/settings', icon: Settings },
    ...visibleSettingsItems(permissions).map((item) => ({ ...item, sub: 'Настройки' })),
  ];
}
